// Browser-safe replacement for the `object-hash` package.
//
// The `object-hash` npm module does `require('crypto')` (Node's crypto), which
// does not exist in the browser and could not be reliably polyfilled through the
// Vite dep optimizer during the CRA->Vite migration. In this codebase object-hash
// is only used to derive a stable string from a JS value for equality checks and
// as React element ids/keys (see Handrails, Info, AssetManager, ImageRenderer,
// BlueprintBuilder). A deterministic serialize + non-cryptographic digest is
// sufficient for those uses and avoids any Node built-in dependency.
//
// The output is NOT compatible with object-hash's sha1 hex output, but nothing in
// the app persists or compares these hashes across the object-hash/this boundary.

// Stable stringify: sorts object keys so key order does not change the result,
// mirroring object-hash's default of hashing structure + values.
function stableStringify(value, seen) {
  if (value === null) return 'null'
  const type = typeof value

  if (type === 'number' || type === 'boolean') return String(value)
  if (type === 'string') return JSON.stringify(value)
  if (type === 'undefined') return 'undefined'
  if (type === 'function') return `fn:${value.name || 'anonymous'}`
  if (type === 'bigint') return `${value}n`
  if (type === 'symbol') return value.toString()

  // Objects and arrays: guard against circular references.
  seen = seen || new WeakSet()
  if (seen.has(value)) return '[Circular]'
  seen.add(value)

  let result
  if (Array.isArray(value)) {
    result = `[${value.map((item) => stableStringify(item, seen)).join(',')}]`
  } else {
    const keys = Object.keys(value).sort()
    result = `{${keys
      .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key], seen)}`)
      .join(',')}}`
  }

  seen.delete(value)
  return result
}

// FNV-1a 32-bit hash -> unsigned hex string. Fast, dependency-free, stable.
function fnv1a(str) {
  let hash = 0x811c9dc5
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i)
    // hash *= 16777619 (FNV prime), kept in 32-bit range.
    hash = (hash + ((hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24))) >>> 0
  }
  return hash.toString(16).padStart(8, '0')
}

export default function objectHash(value) {
  return fnv1a(stableStringify(value))
}
